import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';

import type {ArticleDetails} from '@/modules/article/shared/types';
import {Container} from '@/modules/foundation/components/container';
import {internalLinkToHref} from '@/modules/foundation/sanity/link/query';
import type {KeyedArray} from '@/modules/foundation/shared/types';

import {DesktopNav} from './DesktopNav';
import {MobileNav} from './MobileNav';

export type NavGroupLink = {
	title: string;
	href: string;
};

type NavGroup = {
	links: KeyedArray<NavGroupLink>;
};

export type NavItem = {
	title: string;
	href: string;
	areas: NavGroup;
	resources: NavGroup;
	nemnd: NavGroup;
	articles?: KeyedArray<ArticleDetails>;
};

export type Navigation = {
	insurance: NavItem;
	bank: NavItem;
	collection: NavItem;
	about: {
		title: string;
	};
	nemndspraksisLink?: string;
	financeFrontpageTitle: string;
	loginFrontpageTitle: string;
	forLawyersFrontpageTitle: string;
};

type NavbarProps = {
	navigation: Navigation;
};

export const FRONT_PAGE = 'Til forsiden';

export const Navbar = ({navigation}: NavbarProps) => {
	return (
		<header className={clsx('text-primary-5')}>
			<nav>
				<div className={clsx('border-b', 'border-grey-80', 'max-[1070px]:hidden')}>
					<Container>
						<div
							className={clsx(
								'uppercase',
								'relative',
								'text-[11px]',
								'text-right',
								'py-2.5',
								'flex',
								'flex-wrap',
								'gap-x-6',
								'justify-end',
							)}
						>
							<Link
								href={internalLinkToHref('forLawyers-frontpage')}
								className={clsx('hover:text-link', 'transition-colors')}
							>
								{navigation.forLawyersFrontpageTitle}
							</Link>

							<Link
								href={internalLinkToHref('finance-frontpage')}
								className={clsx('hover:text-link', 'transition-colors')}
							>
								{navigation.financeFrontpageTitle}
							</Link>

							{navigation.nemndspraksisLink && (
								<a
									href={navigation.nemndspraksisLink}
									target="_blank"
									className={clsx('hover:text-link', 'transition-colors')}
								>
									Nemndpraksis
								</a>
							)}
						</div>
					</Container>
				</div>
				<Container className={clsx('relative', 'z-50', 'border-b', 'border-grey-80')}>
					<div
						className={clsx(
							'flex',
							'h-[60px]',
							'min-[1070px]:h-32',
							'items-center',
							'justify-between',
							'gap-[88px]',
						)}
					>
						<Link href="/" className={clsx('inline-block', 'max-w-[250px]')}>
							<span className={clsx('sr-only')}>{FRONT_PAGE}</span>
							<Image
								className="hidden min-[1070px]:block"
								src="/images/logo-primary.svg"
								alt=""
								width={300}
								height={58}
							/>
							<Image
								className="min-[1070px]:hidden"
								src="/images/finkn-logo-short.svg"
								alt=""
								width={67}
								height={23}
							/>
						</Link>
						{navigation ? (
							<>
								<DesktopNav navigation={navigation} />
								<MobileNav navigation={navigation} />
							</>
						) : null}
					</div>
				</Container>
			</nav>
		</header>
	);
};
